import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, Loader2, Heart, Crown, XCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth, API } from '../App';
import axios from 'axios';
import { toast } from 'sonner';

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('loading'); // loading, success, error
  const [paymentData, setPaymentData] = useState(null);
  const navigate = useNavigate();
  const { token, refreshUser } = useAuth();

  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (!sessionId) {
      setStatus('error');
      return;
    }

    let attempts = 0;
    const maxAttempts = 10;
    const pollInterval = 2000;

    const pollPaymentStatus = async () => {
      try {
        const res = await axios.get(`${API}/payments/status/${sessionId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        setPaymentData(res.data);

        if (res.data.payment_status === 'paid') {
          setStatus('success');
          await refreshUser();
          toast.success('Velkommen til Premium!');
          return;
        } else if (res.data.status === 'expired') {
          setStatus('error');
          return;
        }

        // Continue polling if still pending
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(pollPaymentStatus, pollInterval);
        } else {
          setStatus('error');
        }
      } catch (err) {
        console.error('Error checking payment status:', err);
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(pollPaymentStatus, pollInterval);
        } else {
          setStatus('error');
        }
      }
    };

    pollPaymentStatus();
  }, [sessionId, token, refreshUser]);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="max-w-md w-full bg-card rounded-3xl p-8 text-center border border-border shadow-xl"
      >
        {status === 'loading' && (
          <>
            <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-6">
              <Loader2 className="w-10 h-10 text-primary animate-spin" />
            </div>
            <h2 className="text-2xl font-display font-bold text-foreground mb-2">
              Behandler Betaling
            </h2>
            <p className="text-muted-foreground">
              Vent venligst mens vi bekræfter din betaling...
            </p>
          </>
        )}

        {status === 'success' && (
          <>
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: 'spring', duration: 0.5 }}
              className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6"
            >
              <CheckCircle className="w-12 h-12 text-green-600" />
            </motion.div>
            <h2 className="text-2xl font-display font-bold text-foreground mb-2">
              Betaling Gennemført!
            </h2>
            <p className="text-muted-foreground mb-6">
              Du er nu Premium-medlem. Nyd ubegrænset adgang til alle funktioner!
            </p>
            
            {paymentData && (
              <div className="bg-muted rounded-xl p-4 mb-6 text-left">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Beløb</span>
                  <span className="font-semibold text-foreground">
                    {paymentData.amount?.toFixed(2)} kr {paymentData.currency?.toUpperCase()}
                  </span>
                </div>
              </div>
            )}

            <div className="space-y-3">
              <Link to="/discover" className="block">
                <Button className="w-full rounded-full bg-primary hover:bg-primary/90" data-testid="start-exploring-btn">
                  <Heart className="w-5 h-5 mr-2" />
                  Start med at Udforske
                </Button>
              </Link>
              <Link to="/settings" className="block">
                <Button variant="outline" className="w-full rounded-full">
                  <Crown className="w-5 h-5 mr-2" />
                  Se Dine Fordele
                </Button>
              </Link>
            </div>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="w-20 h-20 rounded-full bg-destructive/10 flex items-center justify-center mx-auto mb-6">
              <XCircle className="w-12 h-12 text-destructive" />
            </div>
            <h2 className="text-2xl font-display font-bold text-foreground mb-2">
              Betalingsproblem
            </h2>
            <p className="text-muted-foreground mb-6">
              Vi kunne ikke bekræfte din betaling. Prøv igen eller kontakt support.
            </p>
            <div className="space-y-3">
              <Link to="/premium" className="block">
                <Button className="w-full rounded-full bg-primary hover:bg-primary/90" data-testid="try-again-btn">
                  Prøv Igen
                </Button>
              </Link>
              <Link to="/discover" className="block">
                <Button variant="outline" className="w-full rounded-full">
                  Tilbage til Appen
                </Button>
              </Link>
            </div>
          </>
        )}
      </motion.div>
    </div>
  );
};

export default PaymentSuccess;
